/**
 * L'état d'un match suivi en direct.
 *
 * La règle de Ligue 1 : CINQ remplacements en TROIS fenêtres, et la mi-temps ne
 * consomme pas de fenêtre. C'est la question qu'on se pose à la 70ᵉ, et un double
 * changement ne coûte qu'une fenêtre — une application qui compte les changements
 * sans compter les fenêtres se trompe précisément là où on la consulte.
 */
export const MAX_CHANGEMENTS = 5;
export const MAX_FENETRES = 3;

export const matchVide = (matchId) => ({ matchId, evenements: [], maj_a: Date.now() });

const identifiant = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

export function ajouter(match, evenement) {
  return {
    ...match,
    evenements: [...match.evenements, { id: identifiant(), ...evenement }].sort((a, b) => (a.minute ?? 0) - (b.minute ?? 0)),
    maj_a: Date.now(),
  };
}

export const retirerEvenement = (match, id) => ({
  ...match,
  evenements: match.evenements.filter((e) => e.id !== id),
  maj_a: Date.now(),
});

export const changements = (match) => match.evenements.filter((e) => e.type === 'chg');

/**
 * Les fenêtres consommées.
 *
 * Deux changements annoncés au même arrêt de jeu n'en coûtent qu'une. On les regroupe
 * donc par minute — c'est l'approximation juste : dans un stade, on saisit un double
 * changement en quelques secondes, et deux changements à la même minute sont toujours
 * le même arrêt de jeu. Ceux de la mi-temps sont gratuits.
 */
function fenetresUtilisees(match) {
  const groupes = new Set();
  for (const c of changements(match)) {
    if (c.miTemps) continue;
    groupes.add(c.minute ?? 0);
  }
  return groupes.size;
}

export function compteurs(match) {
  const faits = changements(match).length;
  const fenetres = fenetresUtilisees(match);
  return {
    changements: faits,
    changementsRestants: Math.max(0, MAX_CHANGEMENTS - faits),
    fenetres,
    fenetresRestantes: Math.max(0, MAX_FENETRES - fenetres),
    epuise: faits >= MAX_CHANGEMENTS || fenetres >= MAX_FENETRES,
  };
}

/** Le score de l'AJA, déduit du journal. Les buts adverses sont saisis à part. */
export function score(match) {
  let nous = 0;
  let eux = 0;
  for (const e of match.evenements) {
    if (e.type === 'but') nous += 1;
    else if (e.type === 'butAdverse') eux += 1;
  }
  return { nous, eux };
}

/** Les événements d'un joueur, pour décorer son jeton. */
export function parJoueur(match) {
  const out = {};
  for (const e of match.evenements) {
    if (e.type === 'but' && e.joueurId) (out[e.joueurId] ??= []).push(e);
    if (e.type === 'passe' && e.joueurId) (out[e.joueurId] ??= []).push(e);
  }
  return out;
}

/** La minute d'entrée de chaque remplaçant : la question de la 88ᵉ. */
export function minutesEntree(match) {
  const out = {};
  for (const c of changements(match)) if (c.entreId) out[c.entreId] = c.minute;
  return out;
}

export function jaunesParJoueur(match) {
  const out = {};
  for (const e of match.evenements) if (e.type === 'jaune' && e.joueurId) out[e.joueurId] = (out[e.joueurId] ?? 0) + 1;
  return out;
}

export function rougesParJoueur(match) {
  const out = {};
  for (const e of match.evenements) if (e.type === 'rouge' && e.joueurId) out[e.joueurId] = (out[e.joueurId] ?? 0) + 1;
  return out;
}

export const LIBELLES = {
  but: 'But', butAdverse: 'But adverse', passe: 'Passe décisive',
  chg: 'Changement', jaune: 'Carton jaune', rouge: 'Carton rouge', note: 'Note',
};
